/* eslint-disable max-lines */
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Routes } from '@angular/router';
import { ScheduleShiftTemplateComponent } from '@app/+authenticated/+schedule/employee/schedule-employee-n-period/schedule-items/scheduled-shift/default/schedule-shift-template.component';
import { SidePanelRouteGuard } from '@app/+authenticated/shared/app-side-panel/side-panel-route.guard';
import { CustomFieldPipe } from '@app/pipes/custom-field.pipe';
import { LocaleDatePipe } from '@app/pipes/locale-date.pipe';
import { RecurringScheduleHtmlPipe } from '@app/pipes/recurring-schedule-html.pipe';
import { DepartmentDeactivationGuard } from '@app/reducers/orm/department/department-deactivation.guard';
import { accessGuard } from '@app/shared/access/access.guard';
import { FirstTimeSubscriptionGuard } from '@app/shared/subscription-plan/first-time-subscription.guard';
import { FeatureFlagGuard } from '@sb/shared/feature';
import { TooltipDirective } from '@sb/tooltip';
import { BadgeComponent } from '@sb/ui/badge';
import { CheckboxComponent } from '@sb/ui/checkbox';
import { DividerDirective } from '@sb/ui/divider';
import { IconComponent } from '@sb/ui/icon';
import { NoticeComponent } from '@sb/ui/notice';
import { SelectComponent } from '@sb/ui/select';
import { QuotaType } from '@shiftbase-com/ts-shared/models';

import { SharedEmployeeModule } from '../+authenticated/+employees/shared/shared-employee.module';
import { SharedTimesheetModule } from '../+authenticated/+timesheet/shared/shared-timesheet.module';
import { PermissionGuard } from '../+authenticated/permission-guard.service';
import { AvatarPermissionGuard } from '../+authenticated/shared/avatar/avatar-permission-guard.service';
import { SharedAuthModule } from '../+authenticated/shared/shared-auth.module';
import { DecimalPipe } from '../pipes/decimal.pipe';
import { KnowledgeBaseArticleLinkModule } from '../pipes/knowledge-base-article-link.module';
import { AvailabilityExistsGuard } from '../reducers/orm/availability/availability-exists.guard';
import { ClockIpAddressExistsGuard } from '../reducers/orm/clock-ip-address/clock-ip-address-exists.guard';
import { ContractTypeExistsGuard } from '../reducers/orm/contract-type/contract-type-exists.guard';
import { DepartmentExistsGuard } from '../reducers/orm/department/department-exists.guard';
import { EventLoadGuard } from '../reducers/orm/event/event-load.guard';
import { ExchangeLoadGuard } from '../reducers/orm/exchange/exchange-load.guard';
import { HolidayExistsGuard } from '../reducers/orm/holiday/holiday-exists.guard';
import { IntegrationAppParamGuard } from '../reducers/orm/integration-apps/integration-app-param.guard';
import { IntegrationAppGuard } from '../reducers/orm/integration-apps/integration-app.guard';
import { IntegrationAppTitle } from '../reducers/orm/integration-apps/integration-app.model';
import { LocationExistsGuard } from '../reducers/orm/location/location-exists.guard';
import { OpenShiftExistsGuard } from '../reducers/orm/open-shift/open-shift-exists.quard';
import { PermissionGroupExistsGuard } from '../reducers/orm/permission-group/permission-group-exists.guard';
import { RateCardExistsGuard } from '../reducers/orm/rate-card/rate-card-exists.guard';
import { RequiredShiftExistsGuard } from '../reducers/orm/required-shift/required-shift-exists.quard';
import { ScheduleExchangeLoadGuard } from '../reducers/orm/schedule/schedule-exchange-load.guard';
import { ScheduleExistsGuard } from '../reducers/orm/schedule/schedule-exists.guard';
import { SkillGroupExistsGuard } from '../reducers/orm/skill-group/skill-group-exists.guard';
import { SkillExistsGuard } from '../reducers/orm/skill/skill-exists.guard';
import { TimesheetExistsGuard } from '../reducers/orm/timesheet/timesheet-exists.guard';
import { CustomFieldGuard } from '../shared/custom-field/custom-field.guard';
import { SharedModule } from '../shared/shared.module';
import { selectedSubscriptionGuard } from '../shared/subscription-plan/selected-subscription.guard';
import { SubscriptionPlanGuard } from '../shared/subscription-plan/subscription-plan.guard';
import { SubscriptionQuotaGuard } from '../shared/subscription-plan/subscription-quota.guard';
import { absenceTargetGuard } from './employee/absence-request/absence-target.guard';
import { canActivateAddTurnoverDialog } from './insights/add-turnover/guards/can-activate-add-turnover-dialog.guard';
import { AppConfigureModule } from './integrations/configure/app-configure.module';
import { integrationRequestRoutes } from './integrations/integration-request/integration-request.routes';
import { modalAccess } from './modal.access';

export const modalRoutes: Routes = [
  {
    path: 'authenticator',
    loadComponent: () => import('./auth/mfa-setup/mfa-setup-modal.component').then((m) => m.MfaSetupModalComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'authenticator/disable',
    loadComponent: () =>
      import('./auth/authenticator-disable/authenticator-disable.component').then(
        (m) => m.AuthenticatorDisableComponent,
      ),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'backup-code',
    loadComponent: () => import('./auth/backup-code/backup-code.component').then((m) => m.BackupCodeComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'backup-email',
    loadComponent: () => import('./auth/backup-email/backup-email.component').then((m) => m.BackupEmailComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'support',
    canActivate: [SidePanelRouteGuard],
    outlet: 'modal',
    // Angular rejects a componentless route on a named outlet, and the guard
    // always redirects to the side panel before children are resolved.
    children: [],
  },
  {
    path: 'availability',
    loadComponent: () =>
      import('../+modals/schedule/availability/availability.component').then((m) => m.AvailabilityComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard, AvailabilityExistsGuard],
    outlet: 'modal',
    data: { ...modalAccess['availability'] },
  },
  {
    path: 'calendar-sync',
    loadComponent: () =>
      import('../+modals/schedule/custom-calendar-sync/custom-calendar-sync.component').then(
        (m) => m.CustomCalendarSyncComponent,
      ),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['calendar-sync'] },
  },
  {
    path: 'calendar-sync/add',
    loadComponent: () =>
      import('../+modals/schedule/custom-calendar-sync-edit/custom-calendar-sync-edit.component').then(
        (m) => m.CustomCalendarSyncEditComponent,
      ),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['calendar-sync/add'] },
  },
  {
    path: 'calendar-sync/:id',
    loadComponent: () =>
      import('../+modals/schedule/custom-calendar-sync-edit/custom-calendar-sync-edit.component').then(
        (m) => m.CustomCalendarSyncEditComponent,
      ),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['calendar-sync/:id'] },
  },
  {
    path: 'send-schedule',
    loadComponent: () =>
      import('../+modals/schedule/send-schedule/send-schedule.component').then((m) => m.SendScheduleComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['send-schedule'] },
  },
  {
    path: 'print-schedule',
    loadComponent: () =>
      import('../+modals/schedule/print-schedule/print-schedule.component').then((m) => m.PrintScheduleComponent),
    outlet: 'modal',
  },
  {
    path: 'publish-schedule/:period/:date',
    loadComponent: () =>
      import('../+modals/schedule/publish-schedule/publish-schedule.component').then((m) => m.PublishScheduleComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['publish-schedule/:period/:date'] },
  },
  {
    path: 'manage-forecast/:date',
    loadComponent: () =>
      import('../+modals/schedule/manage-forecast/manage-forecast.component').then((m) => m.ManageForecastComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['manage-forecast/:date'] },
  },

  {
    path: 'import-forecast',
    loadComponent: () =>
      import('../+modals/schedule/import-forecast/import-forecast.component').then((m) => m.ImportForecastComponent),
    canActivate: [accessGuard],
    outlet: 'modal',
    data: { ...modalAccess['import-forecast'] },
  },

  {
    path: 'manage-budget/:date',
    loadComponent: () =>
      import('../+modals/schedule/manage-budget/manage-budget.component').then((m) => m.ManageBudgetComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['manage-budget/:date'] },
  },
  {
    path: 'shift',
    loadComponent: () => import('../+modals/schedule/shift/shift.component').then((m) => m.ModalShiftComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'shift/:department_id',
    loadComponent: () => import('../+modals/schedule/shift/shift.component').then((m) => m.ModalShiftComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'shift/edit/:shift_id',
    loadComponent: () => import('../+modals/schedule/shift/shift.component').then((m) => m.ModalShiftComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'team',
    loadComponent: () => import('../+modals/schedule/team/team.component').then((m) => m.TeamComponent),
    outlet: 'modal',
    data: {
      quotaType: QuotaType.TEAMS,
    },
    canActivate: [SubscriptionQuotaGuard],
  },
  {
    path: 'team/:department_id',
    loadComponent: () => import('../+modals/schedule/team/team.component').then((m) => m.TeamComponent),
    outlet: 'modal',
    data: {
      quotaType: QuotaType.TEAMS,
    },
    canActivate: [SubscriptionQuotaGuard],
  },
  {
    path: 'team/edit/:team_id',
    loadComponent: () => import('../+modals/schedule/team/team.component').then((m) => m.TeamComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'bulk-message',
    loadComponent: () => import('./employee/message/message.component').then((m) => m.MessageComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'exchange-request',
    loadComponent: () =>
      import('../+modals/schedule/exchange-request/exchange-request.component').then((m) => m.ExchangeRequestComponent),
    outlet: 'modal',
    data: { ...modalAccess['exchange-request'] },
    canActivate: [SubscriptionPlanGuard],
  },
  {
    path: 'exchange-request/:id',
    loadComponent: () =>
      import('../+modals/schedule/exchange-request/view-exchange-request.component').then(
        (m) => m.ViewExchangeRequestComponent,
      ),
    outlet: 'modal',
    data: { ...modalAccess['exchange-request/:id'] },
    canActivate: [ExchangeLoadGuard, SubscriptionPlanGuard],
  },
  {
    path: 'exchange-request/:id/edit',
    loadComponent: () =>
      import('../+modals/schedule/exchange-request/exchange-request.component').then((m) => m.ExchangeRequestComponent),
    canActivate: [PermissionGuard, ExchangeLoadGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['exchange-request/:id/edit'] },
  },
  {
    path: 'schedule-shift',
    loadComponent: () =>
      import('../+modals/schedule/schedule-shift-new/schedule-shift-new.component').then(
        (m) => m.ScheduleShiftNewComponent,
      ),
    outlet: 'modal',
    canActivate: [PermissionGuard],
  },
  {
    path: 'schedule-shift/:id',
    loadComponent: () =>
      import('../+modals/schedule/schedule-shift-view/schedule-shift-view.component').then(
        (m) => m.ScheduleShiftViewComponent,
      ),
    canActivate: [PermissionGuard, ScheduleExistsGuard, ScheduleExchangeLoadGuard],
    outlet: 'modal',
  },
  {
    path: 'schedule-shift/:id/edit',
    loadComponent: () =>
      import('../+modals/schedule/schedule-shift-new/schedule-shift-new.component').then(
        (m) => m.ScheduleShiftNewComponent,
      ),
    canActivate: [PermissionGuard, ScheduleExistsGuard],
    outlet: 'modal',
  },
  {
    path: 'schedule-shift/:id/delete',
    loadComponent: () =>
      import('../+modals/schedule/schedule-shift-delete/schedule-shift-delete.component').then(
        (m) => m.ScheduleShiftDeleteComponent,
      ),
    canActivate: [PermissionGuard, ScheduleExistsGuard],
    outlet: 'modal',
  },
  {
    path: 'event',
    loadComponent: () => import('../+modals/schedule/event/event.component').then((m) => m.EventComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'event/:id',
    loadComponent: () =>
      import('../+modals/schedule/event-view/event-view.component').then((m) => m.EventViewComponent),
    canActivate: [PermissionGuard, EventLoadGuard],
    outlet: 'modal',
  },
  {
    path: 'event/:id/edit',
    loadComponent: () => import('../+modals/schedule/event/event.component').then((m) => m.EventComponent),
    canActivate: [PermissionGuard, EventLoadGuard],
    outlet: 'modal',
  },
  {
    path: 'event/:id/delete',
    loadComponent: () =>
      import('../+modals/schedule/event-delete/event-delete.component').then((m) => m.EventDeleteComponent),
    canActivate: [PermissionGuard, EventLoadGuard],
    outlet: 'modal',
  },
  {
    path: 'schedule-open-shift',
    loadComponent: () =>
      import('../+modals/schedule/schedule-shift-new/schedule-openshift-new/schedule-openshift-new.component').then(
        (m) => m.ScheduleOpenshiftNewComponent,
      ),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['schedule-open-shift'] },
  },
  {
    path: 'schedule-open-shift/:id',
    loadComponent: () =>
      import('../+modals/schedule/schedule-openshift-view/schedule-openshift-view.component').then(
        (m) => m.ScheduleOpenShiftViewComponent,
      ),
    canActivate: [PermissionGuard, OpenShiftExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['schedule-open-shift/:id'] },
  },
  {
    path: 'schedule-open-shift/:id/assign',
    loadComponent: () =>
      import('../+modals/schedule/schedule-openshift-assign/schedule-openshift-assign.component').then(
        (m) => m.ScheduleOpenShiftAssignComponent,
      ),
    canActivate: [PermissionGuard, OpenShiftExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['schedule-open-shift/:id/assign'] },
  },
  {
    path: 'schedule-open-shift/:id/withdraw',
    loadComponent: () =>
      import('../+modals/schedule/schedule-openshift-view/schedule-openshift-withdraw-confirm/schedule-openshift-withdraw-confirm.component').then(
        (m) => m.ScheduleOpenshiftWithdrawConfirmComponent,
      ),
    canActivate: [PermissionGuard, OpenShiftExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['schedule-open-shift/:id/withdraw'] },
  },
  {
    path: 'schedule-open-shift/:id/request',
    loadComponent: () =>
      import('../+modals/schedule/schedule-openshift-view/schedule-openshift-rerequest-confirm/schedule-openshift-rerequest-confirm.component').then(
        (m) => m.ScheduleOpenshiftRerequestConfirmComponent,
      ),
    canActivate: [PermissionGuard, OpenShiftExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['schedule-open-shift/:id/request'] },
  },
  {
    path: 'schedule-open-shift/:id/edit',
    loadComponent: () =>
      import('../+modals/schedule/schedule-shift-new/schedule-openshift-new/schedule-openshift-new.component').then(
        (m) => m.ScheduleOpenshiftNewComponent,
      ),
    canActivate: [PermissionGuard, OpenShiftExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['schedule-open-shift/:id/edit'] },
  },
  {
    path: 'schedule-open-shift/:id/delete',
    loadComponent: () =>
      import('../+modals/schedule/schedule-openshift-delete/schedule-openshift-delete.component').then(
        (m) => m.ScheduleOpenShiftDeleteComponent,
      ),
    canActivate: [PermissionGuard, OpenShiftExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['schedule-open-shift/:id/delete'] },
  },
  {
    path: 'schedule-required-shift',
    loadComponent: () =>
      import('./schedule/schedule-required-shift-new/schedule-required-shift-new.component').then(
        (m) => m.ScheduleRequiredShiftNewComponent,
      ),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['schedule-required-shift'] },
  },
  {
    path: 'schedule-required-shift/:id',
    loadComponent: () =>
      import('../+modals/schedule/schedule-required-shift-view/schedule-required-shift-view.component').then(
        (m) => m.ScheduleRequiredShiftViewComponent,
      ),
    canActivate: [PermissionGuard, RequiredShiftExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['schedule-required-shift/:id'] },
  },
  {
    path: 'schedule-required-shift/:id/edit',
    loadComponent: () =>
      import('./schedule/schedule-required-shift-new/schedule-required-shift-new.component').then(
        (m) => m.ScheduleRequiredShiftNewComponent,
      ),
    canActivate: [PermissionGuard, RequiredShiftExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['schedule-required-shift/:id/edit'] },
  },
  {
    path: 'schedule-required-shift/:id/delete',
    loadComponent: () =>
      import('../+modals/schedule/schedule-required-shift-delete/schedule-required-shift-delete.component').then(
        (m) => m.ScheduleRequiredShiftDeleteComponent,
      ),
    canActivate: [PermissionGuard, RequiredShiftExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['schedule-required-shift/:id/delete'] },
  },
  {
    path: 'news-item',
    loadComponent: () => import('./news/news-item/news-item.component').then((m) => m.NewsItemComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'news-item/:id',
    loadComponent: () => import('./news/news-item/news-item.component').then((m) => m.NewsItemComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'open-close-days/:period/:date',
    loadComponent: () =>
      import('./timesheet/open-close-days/open-close-days.component').then((m) => m.OpenCloseDaysComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['open-close-days/:period/:date'] },
  },
  {
    path: 'timesheet',
    loadComponent: () =>
      import('./timesheet/timesheet-hours/timesheet-hours.component').then((m) => m.TimesheetHoursComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
    data: { ...modalAccess['timesheet'] },
  },
  {
    path: 'timesheet/:id',
    loadComponent: () =>
      import('./timesheet/timesheet-hours-view/timesheet-hours-view.component').then(
        (m) => m.TimesheetHoursViewComponent,
      ),
    canActivate: [PermissionGuard, TimesheetExistsGuard],
    outlet: 'modal',
    data: { ...modalAccess['timesheet/:id'] },
  },
  {
    path: 'timesheet/:id/edit',
    loadComponent: () =>
      import('./timesheet/timesheet-hours/timesheet-hours.component').then((m) => m.TimesheetHoursComponent),
    canActivate: [PermissionGuard, TimesheetExistsGuard],
    outlet: 'modal',
    data: { ...modalAccess['timesheet/:id/edit'] },
  },
  {
    path: 'clock-employee',
    loadComponent: () => import('./employee/clock/clock.component').then((m) => m.ClockComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: {
      ...modalAccess['clock-employee'],
      ownClock: false,
    },
  },
  {
    path: 'clock',
    loadComponent: () => import('./employee/clock/clock.component').then((m) => m.ClockComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['clock'] },
  },
  {
    path: 'clock-switch',
    loadComponent: () => import('./employee/clock-switch/clock-switch.component').then((m) => m.ClockSwitchComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['clock-switch'] },
  },
  {
    path: 'employee-avatar/:id',
    loadComponent: () =>
      import('./employee/employee-avatar/employee-avatar.component').then((m) => m.EmployeeAvatarModalComponent),
    canActivate: [AvatarPermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'quick-user-import',
    loadComponent: () =>
      import('./employee/quick-user-import/quick-user-import.component').then((m) => m.QuickUserImportComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'csv-user-import',
    loadComponent: () =>
      import('./employee/csv-user-import/csv-user-import.component').then((m) => m.CsvUserImportComponent),
    outlet: 'modal',
  },
  {
    path: 'employee-file/:employeeId',
    loadComponent: () =>
      import('./employee/employee-file/employee-file.component').then((m) => m.EmployeeFileComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['employee-file/:employeeId'] },
  },
  {
    path: 'employee-file-upload/:employeeId',
    loadComponent: () =>
      import('./employee/employee-file-upload/employee-file-upload.component').then(
        (m) => m.EmployeeFileUploadComponent,
      ),
    canActivate: [accessGuard],
    outlet: 'modal',
    data: { ...modalAccess['employee-file-upload/:employeeId'] },
  },
  {
    path: 'employee-file-details/:employeeId',
    loadComponent: () =>
      import('./employee/employee-file-details/employee-file-details.component').then(
        (m) => m.EmployeeFileDetailsComponent,
      ),
    canActivate: [accessGuard],
    outlet: 'modal',
    data: { ...modalAccess['employee-file-details/:employeeId'] },
  },
  {
    path: 'employee-file/:employeeId/:attachmentId',
    loadComponent: () =>
      import('./employee/employee-file/employee-file.component').then((m) => m.EmployeeFileComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['employee-file/:employeeId/:attachmentId'] },
  },
  {
    path: 'employee-file-succeed/:employeeId/:attachmentId',
    loadComponent: () =>
      import('./employee/employee-file-succeed/employee-file-succeed.component').then(
        (m) => m.EmployeeFileSucceedComponent,
      ),
    canActivate: [accessGuard],
    outlet: 'modal',
    data: { ...modalAccess['employee-file-succeed/:employeeId/:attachmentId'] },
  },
  {
    path: 'employee-file-preview/:employeeId/:attachmentId',
    loadComponent: () =>
      import('./employee/employee-file-preview/employee-file-preview-modal.component').then(
        (m) => m.EmployeeFilePreviewModalComponent,
      ),
    canActivate: [accessGuard],
    outlet: 'modal',
    data: { ...modalAccess['employee-file-preview/:employeeId/:attachmentId'] },
  },
  {
    path: 'employee-file-preview/:employeeId/:attachmentId/edit',
    loadComponent: () =>
      import('./employee/employee-file-preview/employee-file-preview-modal.component').then(
        (m) => m.EmployeeFilePreviewModalComponent,
      ),
    canActivate: [accessGuard],
    outlet: 'modal',
    data: { ...modalAccess['employee-file-preview/:employeeId/:attachmentId/edit'], mode: 'edit' },
  },
  {
    path: 'employee-note/:employeeId',
    loadComponent: () =>
      import('./employee/employee-note/employee-note.component').then((m) => m.EmployeeNoteComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'employee-note/:employeeId/:noteId',
    loadComponent: () =>
      import('./employee/employee-note/employee-note.component').then((m) => m.EmployeeNoteComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'employee-create',
    loadComponent: () => import('./employee/employee-modal.component').then((m) => m.EmployeeModalComponent),
    outlet: 'modal',
    canActivate: [PermissionGuard],
    data: { ...modalAccess['employee-create'] },
  },
  {
    path: 'minijob-payout',
    loadComponent: () =>
      import('./minijob-payout-dialog/minijob-payout-dialog.component').then((m) => m.MinijobPayoutDialogComponent),
    outlet: 'modal',
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    data: { ...modalAccess['minijob-payout'] },
  },
  {
    path: 'add-absence',
    loadComponent: () =>
      import('./employee/absence-request/manage-absence.component').then((m) => m.ManageAbsenceComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard, absenceTargetGuard()],
    outlet: 'modal',
    data: { ...modalAccess['add-absence'] },
  },
  {
    path: 'absentee-option',
    loadComponent: () =>
      import('../+modals/settings/absence-type/absence-type.component').then((m) => m.AbsenceTypeModalComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['absentee-option'] },
  },
  {
    path: 'absentee-option/:id',
    loadComponent: () =>
      import('../+modals/settings/absence-type/absence-type.component').then((m) => m.AbsenceTypeModalComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['absentee-option/:id'] },
  },
  {
    path: 'time-off-correction/:correctionType/:employeeId',
    loadComponent: () =>
      import('./employee/time-off-correction/time-off-correction.component').then((m) => m.TimeOffCorrectionComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['time-off-correction/:correctionType/:employeeId'] },
  },
  {
    path: 'time-off-correction/:correctionType/:employeeId/:balanceId',
    loadComponent: () =>
      import('./employee/time-off-correction/time-off-correction.component').then((m) => m.TimeOffCorrectionComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['time-off-correction/:correctionType/:employeeId/:balanceId'] },
  },
  {
    path: 'contract',
    loadComponent: () => import('./employee/contract/contract.component').then((m) => m.ContractComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['contract'] },
  },
  {
    path: 'contract/:contractId',
    loadComponent: () => import('./employee/contract/contract.component').then((m) => m.ContractComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['contract/:contractId'] },
  },

  {
    path: 'extension',
    loadComponent: () =>
      import('./contract-extension-modal/contract-extension-modal.component').then(
        (m) => m.ContractExtensionModalComponent,
      ),
    canActivate: [SubscriptionPlanGuard, PermissionGuard],
    data: { ...modalAccess['extension'] },
    outlet: 'modal',
  },
  {
    path: 'company-file',
    loadComponent: () =>
      import('../+modals/settings/company-file/company-file.component').then((m) => m.CompanyFileComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['company-file'] },
  },
  {
    path: 'company-file/:attachmentId',
    loadComponent: () =>
      import('../+modals/settings/company-file/company-file.component').then((m) => m.CompanyFileComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['company-file/:attachmentId'] },
  },

  {
    path: 'permission-group',
    loadComponent: () =>
      import('../+modals/settings/permission-group/permission-group.component').then((m) => m.PermissionGroupComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'permission-group/:id',
    loadComponent: () =>
      import('../+modals/settings/permission-group/permission-group.component').then((m) => m.PermissionGroupComponent),
    canActivate: [PermissionGuard, PermissionGroupExistsGuard],
    outlet: 'modal',
  },

  {
    path: 'location-group',
    loadComponent: () =>
      import('../+modals/settings/location-group/location-group.component').then((m) => m.LocationGroupComponent),
    canActivate: [PermissionGuard],
    outlet: 'modal',
  },
  {
    path: 'location-group/:id',
    loadComponent: () =>
      import('../+modals/settings/location-group/location-group.component').then((m) => m.LocationGroupComponent),
    canActivate: [PermissionGuard, LocationExistsGuard],
    outlet: 'modal',
  },
  {
    path: 'location',
    loadComponent: () => import('../+modals/settings/location/location.component').then((m) => m.LocationComponent),
    canActivate: [PermissionGuard, SubscriptionQuotaGuard],
    outlet: 'modal',
    data: {
      quotaType: QuotaType.DEPARTMENTS,
    },
  },
  {
    path: 'location/:id',
    loadComponent: () => import('../+modals/settings/location/location.component').then((m) => m.LocationComponent),
    canActivate: [PermissionGuard, DepartmentExistsGuard],
    outlet: 'modal',
  },
  {
    path: 'time-off-contract-type',
    loadComponent: () =>
      import('../+modals/settings/time-off-contract-type/time-off-contract-type.component').then(
        (m) => m.TimeOffContractTypeComponent,
      ),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['time-off-contract-type'] },
  },
  {
    path: 'time-off-contract-type/:id',
    loadComponent: () =>
      import('../+modals/settings/time-off-contract-type/time-off-contract-type.component').then(
        (m) => m.TimeOffContractTypeComponent,
      ),
    canActivate: [PermissionGuard, ContractTypeExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['time-off-contract-type/:id'] },
  },
  {
    path: 'skill',
    loadComponent: () => import('../+modals/settings/skill/skill.component').then((m) => m.SkillComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['skill'] },
  },
  {
    path: 'skill/:id',
    loadComponent: () => import('../+modals/settings/skill/skill.component').then((m) => m.SkillComponent),
    canActivate: [PermissionGuard, SkillExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['skill/:id'] },
  },
  {
    path: 'skill-group',
    loadComponent: () =>
      import('../+modals/settings/skill-group/skill-group.component').then((m) => m.SkillGroupComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['skill-group'] },
  },
  {
    path: 'skill-group/:id',
    loadComponent: () =>
      import('../+modals/settings/skill-group/skill-group.component').then((m) => m.SkillGroupComponent),
    canActivate: [PermissionGuard, SkillGroupExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['skill-group/:id'] },
  },
  {
    path: 'rate-card',
    loadComponent: () => import('../+modals/settings/rate-card/rate-card.component').then((m) => m.RateCardComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['rate-card'] },
  },
  {
    path: 'rate-card/copy/:id',
    loadComponent: () => import('../+modals/settings/rate-card/rate-card.component').then((m) => m.RateCardComponent),
    canActivate: [PermissionGuard, RateCardExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: {
      ...modalAccess['rate-card/copy/:id'],
      copy: true,
    },
  },
  {
    path: 'rate-card/:id',
    loadComponent: () => import('../+modals/settings/rate-card/rate-card.component').then((m) => m.RateCardComponent),
    canActivate: [PermissionGuard, RateCardExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['rate-card/:id'] },
  },
  {
    path: 'rates/:id',
    loadComponent: () => import('./timesheet/rates-view/rates-view.component').then((m) => m.RatesViewComponent),
    canActivate: [PermissionGuard, TimesheetExistsGuard],
    outlet: 'modal',
  },
  {
    path: 'surcharge',
    loadComponent: () => import('../+modals/settings/surcharge/surcharge.component').then((m) => m.SurchargeComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['surcharge'] },
  },
  {
    path: 'holiday',
    loadComponent: () => import('../+modals/settings/holiday/holiday.component').then((m) => m.HolidayComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['holiday'] },
  },
  {
    path: 'holiday/:id',
    loadComponent: () => import('../+modals/settings/holiday/holiday.component').then((m) => m.HolidayComponent),
    canActivate: [PermissionGuard, HolidayExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['holiday/:id'] },
  },
  {
    path: 'holiday-group',
    loadComponent: () =>
      import('../+modals/settings/holiday-group/holiday-group.component').then((m) => m.HolidayGroupComponent),
    outlet: 'modal',
    canActivate: [PermissionGuard, SubscriptionPlanGuard, SubscriptionQuotaGuard],
    data: {
      ...modalAccess['holiday-group'],
      quotaType: QuotaType.HOLIDAY_GROUPS,
    },
  },
  {
    path: 'integration',
    loadComponent: () =>
      import('../+modals/integrations/integration/integration.component').then((m) => m.IntegrationComponent),
    canActivate: [SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['integration'] },
  },
  {
    path: 'integration/:id',
    loadComponent: () =>
      import('../+modals/integrations/integration/integration.component').then((m) => m.IntegrationComponent),
    canActivate: [SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['integration/:id'] },
  },
  {
    path: 'tonit-configure',
    loadComponent: () =>
      import('../+modals/integrations/tonit-configure/tonit-configure.component').then(
        (m) => m.TonitConfigureComponent,
      ),
    canActivate: [SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['tonit-configure'] },
  },
  {
    path: 'tonit-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/tonit-configure/tonit-configure.component').then(
        (m) => m.TonitConfigureComponent,
      ),
    canActivate: [SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['tonit-configure/:id'] },
  },
  {
    path: 'app-token',
    loadComponent: () =>
      import('../+modals/integrations/app-token/app-token.component').then((m) => m.AppTokenComponent),
    canActivate: [SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['app-token'] },
  },
  {
    path: 'app-token/:id',
    loadComponent: () =>
      import('../+modals/integrations/app-token/app-token.component').then((m) => m.AppTokenComponent),
    canActivate: [SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['app-token/:id'] },
  },
  {
    path: 'piggy-configure',
    loadComponent: () =>
      import('../+modals/integrations/piggy-configure/piggy-configure.component').then(
        (m) => m.PiggyConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.PIGGY,
    },
  },
  {
    path: 'piggy-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/piggy-configure/piggy-configure.component').then(
        (m) => m.PiggyConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.PIGGY,
    },
  },
  {
    path: 'piggyv2-configure',
    loadComponent: () =>
      import('../+modals/integrations/piggy-v2-configure/piggy-v2-configure.component').then(
        (m) => m.PiggyV2ConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.PIGGY_V2,
    },
  },
  {
    path: 'piggyv2-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/piggy-v2-configure/piggy-v2-configure.component').then(
        (m) => m.PiggyV2ConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.PIGGY_V2,
    },
  },
  {
    path: 'datafox-configure',
    loadComponent: () =>
      import('../+modals/integrations/datafox-configure/datafox-configure.component').then(
        (m) => m.DatafoxConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.DATAFOX,
    },
  },
  {
    path: 'datafox-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/datafox-configure/datafox-configure.component').then(
        (m) => m.DatafoxConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.DATAFOX,
    },
  },
  {
    path: 'oneteam-request',
    loadComponent: () =>
      import('../+modals/integrations/oneteam-configure/oneteam-request.component').then(
        (m) => m.OneteamRequestComponent,
      ),
    canActivate: [PermissionGuard, IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.ONETEAM,
    },
  },
  {
    path: 'oneteam-configure',
    loadComponent: () =>
      import('../+modals/integrations/oneteam-configure/oneteam-configure.component').then(
        (m) => m.OneteamConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.ONETEAM,
    },
  },
  {
    path: 'oneteam-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/oneteam-configure/oneteam-configure.component').then(
        (m) => m.OneteamConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.ONETEAM,
    },
  },
  {
    path: 'datafox-request',
    loadComponent: () =>
      import('../+modals/integrations/datafox-configure/datafox-request.component').then(
        (m) => m.DatafoxRequestComponent,
      ),
    canActivate: [PermissionGuard, IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.DATAFOX,
    },
  },
  {
    path: 'datafox-badge',
    loadComponent: () =>
      import('../+authenticated/+account/+integrations/+datafox/datafox-mapping/datafox-general/add-badge/datafox-badge.component').then(
        (m) => m.DatafoxBadgeComponent,
      ),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['datafox-badge'] },
  },
  {
    path: 'datafox-bulk-badges',
    loadComponent: () =>
      import('../+authenticated/+account/+integrations/+datafox/datafox-mapping/datafox-general/add-bulk-badges/datafox-bulk-badges.component').then(
        (m) => m.DatafoxBulkBadgesComponent,
      ),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['datafox-bulk-badges'] },
  },
  {
    path: 'nmbrs-configure',
    loadComponent: () =>
      import('../+modals/integrations/nmbrs-configure/nmbrs-configure.component').then(
        (m) => m.NmbrsConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.NMBRS,
    },
  },
  {
    path: 'nmbrs-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/nmbrs-configure/nmbrs-configure.component').then(
        (m) => m.NmbrsConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.NMBRS,
    },
  },
  {
    path: 'lightspeed-k-series-configure',
    loadComponent: () =>
      import('../+modals/integrations/lightspeed-k-series-configure/lightspeed-k-series-configure.component').then(
        (m) => m.LightspeedKSeriesConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.LIGHTSPEED_K_SERIES,
    },
  },
  {
    path: 'lightspeed-k-series-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/lightspeed-k-series-configure/lightspeed-k-series-configure.component').then(
        (m) => m.LightspeedKSeriesConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.LIGHTSPEED_K_SERIES,
    },
  },
  {
    path: 'shopify-configure',
    loadComponent: () =>
      import('../+modals/integrations/shopify-configure/shopify-configure.component').then(
        (m) => m.ShopifyConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.SHOPIFY,
    },
  },
  {
    path: 'shopify-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/shopify-configure/shopify-configure.component').then(
        (m) => m.ShopifyConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.SHOPIFY,
    },
  },
  {
    path: 'loketv2-configure',
    loadComponent: () =>
      import('../+modals/integrations/loket-v2/loket-v2-configure.component').then((m) => m.LoketV2ConfigureComponent),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.LOKET_V2,
    },
  },
  {
    path: 'loketv2-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/loket-v2/loket-v2-configure.component').then((m) => m.LoketV2ConfigureComponent),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.LOKET_V2,
    },
  },
  {
    path: 'gastrofix-configure',
    loadComponent: () =>
      import('../+modals/integrations/gastrofix-configure/gastrofix-configure.component').then(
        (m) => m.GastrofixConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.GASTROFIX,
    },
  },
  {
    path: 'gastrofix-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/gastrofix-configure/gastrofix-configure.component').then(
        (m) => m.GastrofixConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.GASTROFIX,
    },
  },
  {
    path: 'gastrofix-request',
    loadComponent: () =>
      import('../+modals/integrations/gastrofix-configure/gastrofix-request.component').then(
        (m) => m.GastrofixRequestComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.GASTROFIX,
    },
  },
  {
    path: 'mplus-configure',
    loadComponent: () =>
      import('../+modals/integrations/mplus-configure/mplus-configure.component').then(
        (m) => m.MPlusConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.MPLUS,
    },
  },
  {
    path: 'mplus-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/mplus-configure/mplus-configure.component').then(
        (m) => m.MPlusConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.MPLUS,
    },
  },
  {
    path: 'mplus-request',
    loadComponent: () =>
      import('../+modals/integrations/mplus-configure/mplus-request.component').then((m) => m.MPlusRequestComponent),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.MPLUS,
    },
  },
  {
    path: 'lightspeed-restaurant-v2-configure',
    loadComponent: () =>
      import('../+modals/integrations/lightspeed-restaurant-v2-configure/lightspeed-restaurant-v2-configure.component').then(
        (m) => m.LightspeedRestaurantV2ConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.LIGHTSPEED_RESTAURANT_V2,
    },
  },
  {
    path: 'lightspeed-restaurant-v2-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/lightspeed-restaurant-v2-configure/lightspeed-restaurant-v2-configure.component').then(
        (m) => m.LightspeedRestaurantV2ConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.LIGHTSPEED_RESTAURANT_V2,
    },
  },
  {
    path: 'untill-configure',
    loadComponent: () =>
      import('../+modals/integrations/untill-configure/untill-configure.component').then(
        (m) => m.UntillConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.UNTILL,
    },
  },
  {
    path: 'untill-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/untill-configure/untill-configure.component').then(
        (m) => m.UntillConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.UNTILL,
    },
  },
  {
    path: 'untillv2-configure',
    loadComponent: () =>
      import('../+modals/integrations/untill-v2-configure/untill-v2-configure.component').then(
        (m) => m.UntillV2ConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.UNTILL_V2,
    },
  },
  {
    path: 'untillv2-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/untill-v2-configure/untill-v2-configure.component').then(
        (m) => m.UntillV2ConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.UNTILL_V2,
    },
  },
  {
    path: 'tomtom-configure',
    loadComponent: () =>
      import('../+modals/integrations/tomtom-configure/tomtom-configure.component').then(
        (m) => m.TomtomConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.TOMTOM,
    },
  },
  {
    path: 'tomtom-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/tomtom-configure/tomtom-configure.component').then(
        (m) => m.TomtomConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.TOMTOM,
    },
  },
  {
    path: 'ncr-configure',
    loadComponent: () =>
      import('../+modals/integrations/ncr-configure/ncr-configure.component').then((m) => m.NcrConfigureComponent),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.NCR,
    },
  },
  {
    path: 'ncr-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/ncr-configure/ncr-configure.component').then((m) => m.NcrConfigureComponent),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.NCR,
    },
  },
  {
    path: 'unitouch-configure',
    loadComponent: () =>
      import('../+modals/integrations/unitouch-configure/unitouch-configure.component').then(
        (m) => m.UnitouchConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.UNITOUCH,
    },
  },
  {
    path: 'unitouch-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/unitouch-configure/unitouch-configure.component').then(
        (m) => m.UnitouchConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.UNITOUCH,
    },
  },
  {
    path: 'euur-configure',
    loadComponent: () =>
      import('../+modals/integrations/euur-configure/euur-configure.component').then((m) => m.EuurConfigureComponent),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.EUUR,
    },
  },
  {
    path: 'euur-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/euur-configure/euur-configure.component').then((m) => m.EuurConfigureComponent),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.EUUR,
    },
  },
  {
    path: 'vectron-configure',
    loadComponent: () =>
      import('../+modals/integrations/vectron-configure/vectron-configure.component').then(
        (m) => m.VectronConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.VECTRON,
    },
  },
  {
    path: 'vectron-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/vectron-configure/vectron-configure.component').then(
        (m) => m.VectronConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.VECTRON,
    },
  },
  {
    path: 'exact-configure',
    loadComponent: () =>
      import('../+modals/integrations/exact/exact-configure/exact-configure.component').then(
        (m) => m.ExactConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.EXACT,
    },
  },
  {
    path: 'exact-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/exact/exact-configure/exact-configure.component').then(
        (m) => m.ExactConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.EXACT,
    },
  },
  {
    path: 'lightspeed-retail-configure',
    loadComponent: () =>
      import('../+modals/integrations/lightspeed-retail/lightspeed-retail-configure.component').then(
        (m) => m.LightspeedRetailConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.LIGHTSPEED_RETAIL,
    },
  },
  {
    path: 'lightspeed-retail-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/lightspeed-retail/lightspeed-retail-configure.component').then(
        (m) => m.LightspeedRetailConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.LIGHTSPEED_RETAIL,
    },
  },
  {
    path: 'datev-configure',
    loadComponent: () =>
      import('../+modals/integrations/datev-configure/datev-configure.component').then(
        (m) => m.DatevConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.DATEV,
    },
  },
  {
    path: 'datev-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/datev-configure/datev-configure.component').then(
        (m) => m.DatevConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.DATEV,
    },
  },
  {
    path: 'easylon-configure',
    loadComponent: () =>
      import('../+modals/integrations/easylon-configure/easylon-configure.component').then(
        (m) => m.EasylonConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.EASYLON,
    },
  },
  {
    path: 'easylon-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/easylon-configure/easylon-configure.component').then(
        (m) => m.EasylonConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.EASYLON,
    },
  },
  {
    path: 'connexie-configure',
    loadComponent: () =>
      import('../+modals/integrations/connexie-configure/connexie-configure.component').then(
        (m) => m.ConnexieConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.CONNEXIE,
    },
  },
  {
    path: 'connexie-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/connexie-configure/connexie-configure.component').then(
        (m) => m.ConnexieConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.CONNEXIE,
    },
  },
  {
    path: 'employes-configure',
    loadComponent: () =>
      import('../+modals/integrations/employes-configure/employes-configure.component').then(
        (m) => m.EmployesConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.EMPLOYES,
    },
  },
  {
    path: 'employes-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/employes-configure/employes-configure.component').then(
        (m) => m.EmployesConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.EMPLOYES,
    },
  },
  {
    path: 'hr-salaris-gemak-configure',
    loadComponent: () =>
      import('../+modals/integrations/hr-en-salaris-gemak-configure/hr-en-salaris-gemak-configure.component').then(
        (m) => m.HrEnSalarisGemakConfigureComponent,
      ),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['hr-salaris-gemak-configure'] },
  },
  {
    path: 'hr-salaris-gemak-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/hr-en-salaris-gemak-configure/hr-en-salaris-gemak-configure.component').then(
        (m) => m.HrEnSalarisGemakConfigureComponent,
      ),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['hr-salaris-gemak-configure/:id'] },
  },
  {
    path: 'afas-configure',
    loadComponent: () =>
      import('../+modals/integrations/afas-configure/afas-configure.component').then((m) => m.AfasConfigureComponent),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.AFAS,
    },
  },
  {
    path: 'afas-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/afas-configure/afas-configure.component').then((m) => m.AfasConfigureComponent),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.AFAS,
    },
  },
  {
    path: 'formitable-configure',
    loadComponent: () =>
      import('../+modals/integrations/formitable-configure/formitable-configure.component').then(
        (m) => m.FormitableConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.FORMITABLE,
    },
  },
  {
    path: 'formitable-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/formitable-configure/formitable-configure.component').then(
        (m) => m.FormitableConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.FORMITABLE,
    },
  },
  {
    path: 'formitable-request',
    loadComponent: () =>
      import('../+modals/integrations/formitable-configure/formitable-request.component').then(
        (m) => m.FormitableRequestComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.FORMITABLE,
    },
  },
  {
    path: 'loon-configure',
    loadComponent: () =>
      import('../+modals/integrations/loon-configure/loon-configure.component').then((m) => m.LoonConfigureComponent),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.LOON_NL,
    },
  },
  {
    path: 'loon-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/loon-configure/loon-configure.component').then((m) => m.LoonConfigureComponent),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.LOON_NL,
    },
  },
  {
    path: 'sd-worx-configure',
    loadComponent: () =>
      import('../+modals/integrations/sd-worx-configure/sd-worx-configure.component').then(
        (m) => m.SdWorxConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.SD_WORX,
    },
  },
  {
    path: 'sd-worx-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/sd-worx-configure/sd-worx-configure.component').then(
        (m) => m.SdWorxConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.SD_WORX,
    },
  },
  {
    path: 'guestplan-request',
    loadComponent: () =>
      import('../+modals/integrations/guestplan-configure/guestplan-request.component').then(
        (m) => m.GuestplanRequestComponent,
      ),
    canActivate: [PermissionGuard, IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.GUESTPLAN,
    },
  },
  {
    path: 'guestplan-configure',
    loadComponent: () =>
      import('../+modals/integrations/guestplan-configure/guestplan-configure.component').then(
        (m) => m.GuestplanConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.GUESTPLAN,
    },
  },
  {
    path: 'guestplan-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/guestplan-configure/guestplan-configure.component').then(
        (m) => m.GuestplanConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.GUESTPLAN,
    },
  },

  {
    path: 'app-configure/:app',
    loadComponent: () =>
      import('../+modals/integrations/configure/app-configure.component').then((m) => m.AppConfigureComponent),
    canActivate: [IntegrationAppParamGuard],
    outlet: 'modal',
  },
  {
    path: 'app-configure/:app/:id',
    loadComponent: () =>
      import('../+modals/integrations/configure/app-configure.component').then((m) => m.AppConfigureComponent),
    canActivate: [IntegrationAppParamGuard],
    outlet: 'modal',
  },

  {
    path: 'holiday-import',
    loadComponent: () =>
      import('../+modals/settings/holiday-import/holiday-import.component').then((m) => m.HolidayImportComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['holiday-import'] },
  },
  {
    path: 'clock-ip-address',
    loadComponent: () =>
      import('../+modals/settings/clock-ip-address/clock-ip-address.component').then((m) => m.ClockIpAddressComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['clock-ip-address'] },
  },
  {
    path: 'clock-ip-address/:id',
    loadComponent: () =>
      import('../+modals/settings/clock-ip-address/clock-ip-address.component').then((m) => m.ClockIpAddressComponent),
    canActivate: [PermissionGuard, ClockIpAddressExistsGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['clock-ip-address/:id'] },
  },
  {
    path: 'kiosk',
    loadComponent: () => import('../+modals/settings/kiosk/kiosk.component').then((m) => m.KioskComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard, SubscriptionQuotaGuard],
    outlet: 'modal',
    data: {
      ...modalAccess['kiosk'],
      quotaType: QuotaType.KIOSKS,
    },
  },
  {
    path: 'kiosk/:id',
    loadComponent: () => import('../+modals/settings/kiosk/kiosk.component').then((m) => m.KioskComponent),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['kiosk/:id'] },
  },
  {
    path: 'custom-fields/:appliesTo',
    loadComponent: () =>
      import('../+modals/settings/custom-fields/custom-fields-modal.component').then(
        (m) => m.CustomFieldsModalComponent,
      ),
    canActivate: [SubscriptionPlanGuard, CustomFieldGuard],
    outlet: 'modal',
    data: { ...modalAccess['custom-fields/:appliesTo'] },
  },
  {
    path: 'custom-fields/:appliesTo/:id',
    loadComponent: () =>
      import('../+modals/settings/custom-fields/custom-fields-modal.component').then(
        (m) => m.CustomFieldsModalComponent,
      ),
    canActivate: [SubscriptionPlanGuard, CustomFieldGuard],
    outlet: 'modal',
    data: { ...modalAccess['custom-fields/:appliesTo/:id'] },
  },
  {
    path: 'absence-restriction',
    loadComponent: () =>
      import('../+modals/settings/absence-restriction/absence-restriction.modal.component').then(
        (m) => m.AbsenceRestrictionModalComponent,
      ),
    canActivate: [SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['absence-restriction'] },
  },
  {
    path: 'absence-restriction/:id',
    loadComponent: () =>
      import('../+modals/settings/absence-restriction/absence-restriction.modal.component').then(
        (m) => m.AbsenceRestrictionModalComponent,
      ),
    canActivate: [SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['absence-restriction/:id'] },
  },
  {
    path: 'overtime-policies',
    loadComponent: () =>
      import('../+modals/settings/overtime-policies/overtime-policy.modal.component').then(
        (m) => m.OvertimePolicyModalComponent,
      ),
    canActivate: [SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['overtime-policies'] },
  },
  {
    path: 'overtime-policies/:id',
    loadComponent: () =>
      import('../+modals/settings/overtime-policies/overtime-policy.modal.component').then(
        (m) => m.OvertimePolicyModalComponent,
      ),
    canActivate: [SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['overtime-policies/:id'] },
  },
  {
    path: 'time-off-balances',
    loadComponent: () =>
      import('../+modals/settings/time-off-balances/time-off-balances-modal.component').then(
        (m) => m.TimeOffBalancesModalComponent,
      ),
    canActivate: [SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['time-off-balances'] },
  },
  {
    path: 'time-off-balances/:id',
    loadComponent: () =>
      import('../+modals/settings/time-off-balances/time-off-balances-modal.component').then(
        (m) => m.TimeOffBalancesModalComponent,
      ),
    canActivate: [SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['time-off-balances/:id'] },
  },
  {
    path: 'report-favorite/save',
    loadComponent: () =>
      import('./report/favorites/create/report-favorite.modal.component').then((m) => m.ReportFavoriteModalComponent),
    outlet: 'modal',
    data: { ...modalAccess['report-favorite/save'] },
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
  },
  {
    path: 'report-favorite/list',
    loadComponent: () =>
      import('./report/favorites/list/report-favorite-list-modal.component').then(
        (m) => m.ReportFavoriteListModalComponent,
      ),
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    outlet: 'modal',
    data: { ...modalAccess['report-favorite/list'] },
  },

  {
    path: 'report-recurring/save',
    loadComponent: () =>
      import('../+modals/report/recurring/save/report-recurring-modal.component').then(
        (m) => m.ReportRecurringModalComponent,
      ),
    outlet: 'modal',
    data: { ...modalAccess['report-recurring/save'] },
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
  },

  {
    path: 'report-recurring/:id/edit',
    loadComponent: () =>
      import('../+modals/report/recurring/save/report-recurring-modal.component').then(
        (m) => m.ReportRecurringModalComponent,
      ),
    outlet: 'modal',
    data: { ...modalAccess['report-recurring/:id/edit'] },
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
  },

  {
    path: 'report-recurring/list',
    loadComponent: () =>
      import('../+modals/report/recurring/list/report-recurring-list-modal.component').then(
        (m) => m.ReportRecurringListModalComponent,
      ),
    outlet: 'modal',
    data: { ...modalAccess['report-recurring/list'] },
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
  },

  {
    path: 'billing/details',
    loadComponent: () =>
      import('../+authenticated/+account/billing/modals/billing-details/billing-details-modal.component').then(
        (m) => m.BillingDetailsModalComponent,
      ),
    outlet: 'modal',
    data: { ...modalAccess['billing/details'] },
    canActivate: [PermissionGuard],
  },
  {
    path: 'billing/plan-wizard',
    loadComponent: () =>
      import('../+authenticated/+account/billing/modals/plan-wizard/plan-wizard-modal.component').then(
        (m) => m.PlanWizardModalComponent,
      ),
    outlet: 'modal',
    data: { ...modalAccess['billing/plan-wizard'] },
    canActivate: [PermissionGuard],
  },
  {
    path: 'hooray-configure',
    loadComponent: () =>
      import('../+modals/integrations/hooray-configure/hooray-configure.component').then(
        (m) => m.HoorayConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.HOORAY,
    },
  },
  {
    path: 'hooray-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/hooray-configure/hooray-configure.component').then(
        (m) => m.HoorayConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.HOORAY,
    },
  },
  {
    path: 'add-absence-policy',
    loadComponent: () =>
      import('./settings/add-absence-policy/add-absence-policy.component').then((m) => m.AddAbsencePolicyComponent),
    outlet: 'modal',
    data: { ...modalAccess['add-absence-policy'] },
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
  },
  {
    path: 'activate-absence-type/:policyId/:absenceTypeId',
    loadComponent: () =>
      import('./settings/activate-absence-type/activate-absence-type.component').then(
        (m) => m.ActivateAbsenceTypeComponent,
      ),
    outlet: 'modal',
    data: { ...modalAccess['activate-absence-type/:policyId/:absenceTypeId'] },
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
  },
  {
    path: 'delete-team/:teamId',
    loadComponent: () => import('./settings/delete-team/delete-team.component').then((m) => m.DeleteTeamComponent),
    outlet: 'modal',
    data: { ...modalAccess['delete-team/:teamId'] },
    canActivate: [PermissionGuard],
  },
  {
    path: 'deactivate-department/:departmentId',
    loadComponent: () =>
      import('./settings/deactivate-department/deactivate-department.component').then(
        (m) => m.DeactivateDepartmentComponent,
      ),
    outlet: 'modal',
    data: { ...modalAccess['deactivate-department/:departmentId'] },
    canActivate: [PermissionGuard, DepartmentDeactivationGuard],
  },
  {
    path: 'bork-request',
    loadComponent: () =>
      import('../+modals/integrations/bork-configure/bork-request.component').then((m) => m.BorkRequestComponent),
    canActivate: [PermissionGuard, IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.BORK,
    },
  },
  {
    path: 'jixbee-request',
    loadComponent: () =>
      import('../+modals/integrations/jixbee-configure/jixbee-request.component').then((m) => m.JixbeeRequestComponent),
    canActivate: [PermissionGuard, IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.JIXBEE,
    },
  },
  {
    path: 'nextup-request',
    loadComponent: () =>
      import('../+modals/integrations/nextup-configure/nextup-request.component').then((m) => m.NextUpRequestComponent),
    canActivate: [PermissionGuard, IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.ZENCHEF,
    },
  },
  {
    path: 'zenchef-request',
    loadComponent: () =>
      import('../+modals/integrations/zenchef-configure/zenchef-request.component').then(
        (m) => m.ZenchefRequestComponent,
      ),
    canActivate: [PermissionGuard, IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.ZENCHEF,
    },
  },
  {
    path: 'zenchef-configure',
    loadComponent: () =>
      import('../+modals/integrations/zenchef-configure/zenchef-configure.component').then(
        (m) => m.ZenchefConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.ZENCHEF,
    },
  },
  {
    path: 'zenchef-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/zenchef-configure/zenchef-configure.component').then(
        (m) => m.ZenchefConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.ZENCHEF,
    },
  },
  {
    path: 'caoapi-request',
    loadComponent: () =>
      import('../+modals/integrations/caoapi-configure/caoapi-request.component').then((m) => m.CaoApiRequestComponent),
    canActivate: [PermissionGuard, IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.CAOAPI,
    },
  },
  {
    path: 'mice-request',
    loadComponent: () =>
      import('../+modals/integrations/mice-configure/mice-request.component').then((m) => m.MiceRequestComponent),
    canActivate: [PermissionGuard, IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.MICE,
    },
  },
  {
    path: 'sides-configure',
    loadComponent: () =>
      import('../+modals/integrations/sides-configure/sides-configure.component').then(
        (m) => m.SidesConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.SIDES,
    },
  },
  {
    path: 'sides-configure/:id',
    loadComponent: () =>
      import('../+modals/integrations/sides-configure/sides-configure.component').then(
        (m) => m.SidesConfigureComponent,
      ),
    canActivate: [IntegrationAppGuard],
    outlet: 'modal',
    data: {
      appTitle: IntegrationAppTitle.SIDES,
    },
  },
  {
    path: 'onboarding-invite-employees',
    loadComponent: () =>
      import('../+onboarding/components/invite-employees/invite-employees.component').then(
        (m) => m.InviteEmployeesDialogComponent,
      ),
    canActivate: [PermissionGuard],
    outlet: 'modal',
    data: { ...modalAccess['onboarding-invite-employees'] },
  },
  {
    path: 'absence-request/:id',
    loadComponent: () =>
      import('./employee/absence-request/absence-request.component').then((m) => m.AbsenceRequestComponent),
    canActivate: [PermissionGuard, absenceTargetGuard()],
    outlet: 'modal',
    data: { ...modalAccess['absence-request/:id'] },
  },
  {
    path: 'absence-edit/:id',
    loadComponent: () =>
      import('./employee/absence-request/manage-absence.component').then((m) => m.ManageAbsenceComponent),
    canActivate: [PermissionGuard, absenceTargetGuard({ reuseStoredAbsence: true })],
    outlet: 'modal',
    data: { ...modalAccess['absence-edit/:id'] },
  },
  {
    path: 'subscription',
    loadComponent: () =>
      import('../+authenticated/+account/billing/modals/subscription/subscription-dialog.component').then(
        (m) => m.SubscriptionDialogComponent,
      ),
    canActivate: [PermissionGuard, FirstTimeSubscriptionGuard],
    outlet: 'modal',
    data: { ...modalAccess['subscription'] },
  },
  {
    path: 'subscription-confirmation',
    loadComponent: () =>
      import('../+authenticated/+account/billing/modals/payment-confirmation-dialog/payment-confirmation-dialog.component').then(
        (m) => m.PaymentConfirmationDialogComponent,
      ),
    canActivate: [PermissionGuard, selectedSubscriptionGuard],
    outlet: 'modal',
    data: { ...modalAccess['subscription-confirmation'] },
  },
  {
    path: 'highlights',
    loadComponent: () =>
      import('../+onboarding/highlights-dialog/highlights-dialog.component').then((m) => m.HighlightsDialogComponent),
    outlet: 'modal',
  },
  {
    path: 'onboarding-welcome',
    loadComponent: () =>
      import('../+onboarding/onboarding-welcome-dialog/onboarding-welcome-dialog.component').then(
        (m) => m.OnboardingWelcomeDialogComponent,
      ),
    outlet: 'modal',
    canActivate: [FeatureFlagGuard],
    data: { ...modalAccess['onboarding-welcome'] },
  },
  {
    path: 'add-turnover',
    loadComponent: () =>
      import('./insights/add-turnover/add-manual-turnover.component').then((m) => m.AddManualTurnoverComponent),
    outlet: 'modal',
    canActivate: [PermissionGuard, canActivateAddTurnoverDialog],
    data: { ...modalAccess['add-turnover'] },
  },
  {
    path: 'import-turnover',
    loadComponent: () =>
      import('./insights/import-turnover/import-turnover.component').then((m) => m.ImportTurnoverComponent),
    outlet: 'modal',
    canActivate: [PermissionGuard, SubscriptionPlanGuard],
    data: { ...modalAccess['import-turnover'] },
  },
  {
    path: 'diary',
    loadComponent: () => import('./diary/diary-dialog/diary-dialog.component').then((m) => m.DiaryDialogComponent),
    outlet: 'modal',
    canActivate: [PermissionGuard],
    data: { ...modalAccess['diary'] },
  },
  {
    path: 'diary-view',
    loadComponent: () =>
      import('./diary/diary-view-dialog/diary-view-dialog.component').then((m) => m.DiaryViewDialogComponent),
    outlet: 'modal',
    canActivate: [PermissionGuard],
    data: { ...modalAccess['diary-view'] },
  },

  ...integrationRequestRoutes,
];

@NgModule({
  imports: [
    SharedAuthModule,
    RouterModule.forChild(modalRoutes),
    SharedTimesheetModule,
    SharedModule,
    SharedEmployeeModule,
    ReactiveFormsModule,
    KnowledgeBaseArticleLinkModule,
    AppConfigureModule,
    CheckboxComponent,
    DecimalPipe,
    ScheduleShiftTemplateComponent,
    NoticeComponent,
    CustomFieldPipe,
    IconComponent,
    TooltipDirective,
    BadgeComponent,
    RecurringScheduleHtmlPipe,
    SelectComponent,
    LocaleDatePipe,
    DividerDirective,
  ],
  providers: [CustomFieldGuard],
  declarations: [],
})
export class ModalModule {}
